import type { Theme } from "@mui/material/styles";
import type { AppSx } from "../types";

export const TABLE_ROW_GRID = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr auto",
    sm: "minmax(0, 2.6fr) minmax(0, 1.3fr) minmax(0, 1.3fr) 40px",
  },
  columnGap: 1,
  rowGap: 0.75,
  alignItems: "start",
} as const;

export const TABLE_CELL_INPUT_SX = {
  m: 0,
  "& .MuiInputBase-root": {
    minHeight: 40,
    height: 40,
    boxSizing: "border-box",
  },
  "& .MuiOutlinedInput-input, & .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    py: 0,
    minHeight: "unset",
  },
  "& .MuiAutocomplete-input": {
    py: "0 !important",
  },
} as const;

export interface TableColumnsEditorComponentStyles {
  tableCellInput: AppSx;
  fieldAutocomplete: AppSx;
  fieldAutocompleteInput: AppSx;
  root: AppSx;
  headerRow: AppSx;
  emptyState: AppSx;
  dataRow: (hasBorder: boolean) => AppSx;
  fieldCol: AppSx;
  mobileLabel: AppSx;
  optionTypeCaption: AppSx;
  fieldHelperText: AppSx;
  compositeFieldHint: AppSx;
  deleteButtonCol: AppSx;
  deleteCol: AppSx;
  customFormatStack: AppSx;
  advancedStack: AppSx;
  barFillStack: AppSx;
  formulaPicker: AppSx;
  formulaPickerCategory: AppSx;
  formulaPickerToken: AppSx;
  formulaPickerApply: AppSx;
  formulaPickerListbox: AppSx;
  formulaPickerPopper: AppSx;
  formulaPickerOption: AppSx;
}

export function buildTableColumnsEditorComponentStyles(_theme: Theme): TableColumnsEditorComponentStyles {
  return {
    tableCellInput: TABLE_CELL_INPUT_SX,
    fieldAutocomplete: {
      width: "100%",
      "& .MuiAutocomplete-tag": {
        maxWidth: "100%",
      },
    },
    fieldAutocompleteInput: {
      m: 0,
      "& .MuiInputBase-root": {
        minHeight: 40,
        height: "auto",
        alignItems: "flex-start",
        py: 0.5,
        boxSizing: "border-box",
      },
      "& .MuiAutocomplete-inputRoot": {
        flexWrap: "wrap",
        gap: 0.25,
        py: "4px !important",
      },
      "& .MuiOutlinedInput-input, & .MuiAutocomplete-input": {
        py: "2px !important",
        minWidth: 80,
      },
      "& .MuiFormHelperText-root": {
        display: "none",
      },
    },
    root: {
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      overflow: "hidden",
      bgcolor: "background.paper",
    },
    headerRow: {
      ...TABLE_ROW_GRID,
      display: { xs: "none", sm: "grid" },
      px: 1.25,
      py: 0.75,
      bgcolor: "action.hover",
      borderBottom: 1,
      borderColor: "divider",
    },
    emptyState: { px: 1.5, py: 2, textAlign: "center" },
    dataRow: (hasBorder) => ({
      px: 1.25,
      py: 1,
      borderBottom: hasBorder ? 1 : 0,
      borderColor: "divider",
      "&:hover": { bgcolor: "action.hover" },
    }),
    fieldCol: { gridColumn: { xs: "1 / -1", sm: "auto" }, minWidth: 0 },
    mobileLabel: { display: { xs: "block", sm: "none" }, mb: 0.5 },
    optionTypeCaption: { ml: 1 },
    fieldHelperText: { display: "block", mt: 0.5, minHeight: 16 },
    compositeFieldHint: { display: "block", mt: 0.25 },
    deleteButtonCol: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      pt: 0.5,
      gridColumn: { xs: "2", sm: "auto" },
      alignSelf: { xs: "end", sm: "start" },
    },
    deleteCol: { display: "flex", justifyContent: "flex-end" },
    customFormatStack: { mt: 0.75, pl: { sm: 0 } },
    advancedStack: {
      mt: 1,
      pt: 1,
      borderTop: 1,
      borderColor: "divider",
    },
    barFillStack: {
      pl: { sm: 1 },
      borderLeft: { sm: 2 },
      borderColor: { sm: "divider" },
    },
    formulaPicker: { width: "100%", alignItems: "flex-end" },
    formulaPickerCategory: {
      minWidth: 280,
      flex: "1 0 280px",
      "& .MuiFormControl-root": { width: "100%" },
      "& .MuiAutocomplete-input": { minWidth: "16ch !important" },
    },
    formulaPickerToken: {
      minWidth: 420,
      flex: "1.8 0 420px",
      "& .MuiFormControl-root": { width: "100%" },
      "& .MuiAutocomplete-input": { minWidth: "24ch !important" },
    },
    formulaPickerApply: { height: 40, px: 1.5, flex: "0 0 auto" },
    formulaPickerListbox: { maxHeight: 320 },
    formulaPickerPopper: {
      zIndex: 1400,
      minWidth: 280,
    },
    formulaPickerOption: {
      alignItems: "flex-start",
      whiteSpace: "nowrap",
      "& .MuiListItemText-primary, & .MuiListItemText-secondary": {
        overflow: "visible",
        textOverflow: "clip",
        whiteSpace: "nowrap",
      },
    },
  };
}
